<!--
  Client Portal v2: Budget (real data + Client-Admin cap management).

  Build contract:
  docs/planning/2026-06-30-client-portal-v2-data-api-refactor.md section 5.2,
  decision 7 (soft target -- over-budget shows red for visibility, never
  blocks). Wired to GET /api/portal/budget: per-department spend vs cap for
  the current fiscal period, read from the real budget_periods + budgets rows.

  CAP MANAGEMENT (Jake product-authority call 2026-06-30 -- a DELIBERATE
  departure from the v2 view-only "budget editing deferred" decision): a
  Client Admin can SET / ADJUST each department's budget cap + cadence
  in-portal via PUT /api/portal/departments/:id/budget. Distributors invoice
  clients for non-external spend, so the client self-governs its own caps.
  Store Managers do NOT see the edit affordance (they lack
  portal.manage_budgets). Adjusting a cap NEVER zeroes historical spend
  (server preserves period spent_amount). Allowances stay distributor-
  configured (out of scope).

  Empty state: a client whose departments have no budgets configured (e.g. a
  card-pay-only client) shows a "no budgets configured" message rather than
  an empty table.
-->
<template>
  <v-container fluid class="portal-budget" data-testid="portal-page-budget">
    <h1 class="text-h5 mb-4">Budget</h1>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      data-testid="budget-loading"
    />

    <v-alert
      v-else-if="errorMsg"
      type="error"
      variant="tonal"
      data-testid="budget-error"
    >
      {{ errorMsg }}
    </v-alert>

    <v-card
      v-else-if="!budgetLines.length"
      class="pa-6 text-center"
      data-testid="budget-empty"
    >
      <div class="text-subtitle-1 mb-1">No budgets configured</div>
      <div class="text-body-2 text-medium-emphasis">
        No spending cap has been set yet. Stores that run on individual card
        payment do not need a budget.
      </div>
    </v-card>

    <v-alert
      v-if="actionMsg"
      :type="actionOk ? 'success' : 'error'"
      variant="tonal"
      class="mb-3"
      density="compact"
      data-testid="budget-action-msg"
    >
      {{ actionMsg }}
    </v-alert>

    <v-card v-else>
      <v-table data-testid="budget-table">
        <thead>
          <tr>
            <!-- Departments OFF: the single overall row has no department
                 to name, so the column is dropped rather than left blank. -->
            <th v-if="!isOverallView">Department</th>
            <th>Period</th>
            <th>Spent</th>
            <th>Cap</th>
            <th style="width: 30%">Utilization</th>
            <th v-if="canManageBudgets"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="line in budgetLines"
            :key="rowKey(line)"
            :data-testid="`budget-row-${rowKey(line)}`"
          >
            <td v-if="!isOverallView">{{ line.department_name }}</td>
            <td>{{ formatPeriod(line) }}</td>
            <td>{{ formatDollars(line.spent_cents) }}</td>
            <td>{{ formatDollars(line.cap_cents) }}</td>
            <td>
              <v-progress-linear
                :model-value="percentUsed(line)"
                :color="barColor(line)"
                height="10"
                :data-testid="`budget-progress-${rowKey(line)}`"
              />
              <span class="text-caption text-medium-emphasis">
                {{ percentUsed(line).toFixed(0) }}% used
                <span
                  v-if="line.over_budget"
                  class="budget-over"
                  :data-testid="`budget-over-${rowKey(line)}`"
                >
                  -- over budget
                </span>
              </span>
            </td>
            <!-- Client-Admin ONLY: adjust this department's cap + cadence. -->
            <td v-if="canManageBudgets" class="budget-edit-cell">
              <PortalButton
                variant="ghost"
                size="sm"
                :data-testid="`budget-edit-${rowKey(line)}`"
                @click="openEdit(line)"
              >
                Edit cap
              </PortalButton>
            </td>
          </tr>
        </tbody>
        <!-- Client-level TOTAL row (D6-polish). Sums cap + spent across all
             departments; the % matches the sidebar Budget sub-text because
             both read the backend's total_pct (spent/cap over the same
             budgets). Zero-cap departments are excluded from the denominator
             server-side. -->
        <tfoot v-if="!isOverallView">
          <tr class="budget-total" data-testid="budget-total-row">
            <td class="budget-total__label">Total</td>
            <td></td>
            <td class="budget-total__num">
              {{ formatDollars(totalSpentCents) }}
            </td>
            <td class="budget-total__num">
              {{ formatDollars(totalCapCents) }}
            </td>
            <td>
              <v-progress-linear
                :model-value="totalPercentBar"
                :color="totalBarColor"
                height="10"
                data-testid="budget-total-progress"
              />
              <span class="text-caption budget-total__pct">
                {{ totalPct }}% used
                <span
                  v-if="totalOverBudget"
                  class="budget-over"
                  data-testid="budget-total-over"
                >
                  -- over budget
                </span>
              </span>
            </td>
            <td v-if="canManageBudgets"></td>
          </tr>
        </tfoot>
      </v-table>
    </v-card>

    <!-- ===== Edit-cap dialog (Client-Admin only) ===== -->
    <v-dialog v-model="dialogOpen" max-width="440" data-testid="budget-dialog">
      <v-card class="pa-5">
        <div class="text-subtitle-1 mb-1">Edit budget cap</div>
        <div class="text-body-2 text-medium-emphasis mb-3">
          Set the spending cap for
          <strong>{{ dialogDeptName }}</strong>. Adjusting the cap does not
          change spend already recorded this period.
        </div>
        <!-- Departments OFF: the overall cap is distributed back across the
             underlying department budgets in proportion to their current
             caps. Say so, rather than letting the number appear to change
             something the client cannot see. -->
        <div
          v-if="isOverallView"
          class="text-body-2 text-medium-emphasis mb-3"
          data-testid="budget-overall-hint"
        >
          This is your company-wide budget. Spend from every department
          counts against it.
        </div>
        <v-text-field
          v-model="dialogCap"
          label="Cap amount (USD)"
          type="number"
          min="0"
          variant="outlined"
          density="compact"
          data-testid="budget-cap-input"
        />
        <v-select
          v-model="dialogCadence"
          :items="cadenceOptions"
          label="Cadence"
          variant="outlined"
          density="compact"
          data-testid="budget-cadence-input"
        />
        <div
          v-if="dialogError"
          class="text-body-2 budget-dialog__error mb-2"
          data-testid="budget-dialog-error"
        >
          {{ dialogError }}
        </div>
        <div class="budget-dialog__actions">
          <PortalButton
            variant="ghost"
            size="sm"
            data-testid="budget-dialog-cancel"
            @click="closeDialog"
          >
            Cancel
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            :loading="dialogSaving"
            data-testid="budget-dialog-save"
            @click="submitDialog"
          >
            Save cap
          </PortalButton>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "@/axios";
import PortalButton from "@/components/portal/PortalButton.vue";
import { useAccountStore } from "@/stores/account";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";

export default {
  name: "PortalBudget",
  components: { PortalButton },
  data() {
    return {
      budgetLines: [],
      // Client-level totals from the /api/portal/budget payload. Backend is
      // the source of truth so the total-row % matches the sidebar Budget
      // sub-text exactly (same total_pct). Fallbacks compute from the lines
      // for resilience if an older payload omits them.
      totals: null,
      loading: true,
      errorMsg: "",
      // Cadence vocabulary -- mirrors the backend BUDGET_CADENCES.
      cadenceOptions: ["monthly", "quarterly", "annual"],
      // Edit-cap dialog state.
      dialogOpen: false,
      dialogDeptId: null,
      dialogDeptName: "",
      // True when the dialog is editing the derived OVERALL cap rather
      // than one department's -- selects the client-level PUT target.
      dialogIsOverall: false,
      dialogCap: "",
      dialogCadence: "monthly",
      dialogSaving: false,
      dialogError: "",
      // Post-action banner.
      actionMsg: "",
      actionOk: true,
    };
  },
  created() {
    this.fetchBudget();
  },
  computed: {
    // Client-Admin-only cap editing gates on the NEW self-service cap
    // (Jake 2026-06-30). Store Managers lack it -> read-only (no affordance).
    // Reactive: re-renders when the account store hydrates.
    canManageBudgets() {
      const account = useAccountStore();
      return account.capabilities.includes("portal.manage_budgets");
    },
    // Departments OFF (Jake, 2026-08-24): the backend collapses the table
    // to ONE row flagged ``is_overall``. The page reads THAT flag rather
    // than fetching the feature toggle itself -- one mechanism, and the
    // row shape cannot disagree with the flag that produced it.
    isOverallView() {
      return this.budgetLines.length === 1 && !!this.budgetLines[0].is_overall;
    },
    totalCapCents() {
      if (this.totals && this.totals.total_cap_cents != null) {
        return this.totals.total_cap_cents;
      }
      // Fallback: sum only capped departments (mirrors the backend rule).
      return this.budgetLines.reduce(
        (sum, l) => sum + (l.cap_cents > 0 ? l.cap_cents : 0),
        0,
      );
    },
    totalSpentCents() {
      if (this.totals && this.totals.total_spent_cents != null) {
        return this.totals.total_spent_cents;
      }
      return this.budgetLines.reduce((sum, l) => sum + (l.spent_cents || 0), 0);
    },
    totalPct() {
      if (this.totals && this.totals.total_pct != null) {
        return this.totals.total_pct;
      }
      const cap = this.totalCapCents;
      if (!cap) return 0;
      return Math.round((this.totalSpentCents / cap) * 100);
    },
    totalOverBudget() {
      if (this.totals && this.totals.total_over_budget != null) {
        return this.totals.total_over_budget;
      }
      return this.totalSpentCents > this.totalCapCents;
    },
    // The progress bar caps its fill at 100 even when over budget.
    totalPercentBar() {
      return Math.min(100, this.totalPct);
    },
    totalBarColor() {
      if (this.totalOverBudget || this.totalPct >= 90) return "error";
      if (this.totalPct >= 75) return "warning";
      return "primary";
    },
  },
  methods: {
    async fetchBudget() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const resp = await axios.get("/api/portal/budget");
        this.budgetLines = resp?.data?.budgets ?? [];
        this.totals = resp?.data ?? null;
      } catch (e) {
        this.errorMsg = "Could not load your budgets.";
      } finally {
        this.loading = false;
      }
    },
    // Stable per-row key/testid. The overall row has NO department id
    // (it is derived from many), so it keys on the literal "overall"
    // rather than on a null that would collide across rows.
    rowKey(line) {
      return line && line.is_overall ? "overall" : line.department_id;
    },
    openEdit(line) {
      this.dialogDeptId = line.department_id;
      this.dialogDeptName = line.is_overall
        ? "your overall budget"
        : line.department_name;
      this.dialogIsOverall = !!line.is_overall;
      // Pre-fill the current cap in dollars + the current cadence.
      this.dialogCap = ((line.cap_cents || 0) / 100).toFixed(2);
      this.dialogCadence = line.cadence || "monthly";
      this.dialogError = "";
      this.dialogOpen = true;
    },
    closeDialog() {
      this.dialogOpen = false;
      this.dialogSaving = false;
    },
    async submitDialog() {
      if (this.dialogSaving) return;
      const cap = Number(this.dialogCap);
      if (!Number.isFinite(cap) || cap < 0) {
        this.dialogError = "Enter a cap amount of 0 or more.";
        return;
      }
      if (!this.cadenceOptions.includes(this.dialogCadence)) {
        this.dialogError = "Choose a cadence.";
        return;
      }
      this.dialogSaving = true;
      this.dialogError = "";
      try {
        // Overall view -> the CLIENT-level endpoint, which distributes the
        // new total proportionally across the underlying department
        // budgets server-side. There is no department id to address here.
        const url = this.dialogIsOverall
          ? "/api/portal/budget"
          : `/api/portal/departments/${this.dialogDeptId}/budget`;
        await axios.put(url, {
          cap_amount: cap,
          cadence: this.dialogCadence,
        });
        this.dialogOpen = false;
        this.actionMsg = "Budget cap updated.";
        this.actionOk = true;
        await this.fetchBudget();
        // Refresh the sidebar Budget sub-text (budget_pct) live (#1830).
        // Generic delegated mechanism (PR-E): the portalNavSummary store
        // subscribes and refetches.
        emitSidebarRefresh("budget");
      } catch (e) {
        const status = e?.response?.status;
        if (status === 400) {
          this.dialogError = "Enter a valid cap amount and cadence.";
        } else if (status === 403 || status === 404) {
          this.dialogError = "You cannot edit this department's budget.";
        } else {
          this.dialogError = "Could not save. Please try again.";
        }
      } finally {
        this.dialogSaving = false;
      }
    },
    formatDollars(cents) {
      return `$${((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    formatPeriod(line) {
      const cadence = line.cadence ? `${line.cadence}` : "";
      if (line.period_start && line.period_end) {
        return `${cadence} (${line.period_start} - ${line.period_end})`.trim();
      }
      return cadence || "--";
    },
    percentUsed(line) {
      if (!line.cap_cents) return 0;
      return Math.min(100, (line.spent_cents / line.cap_cents) * 100);
    },
    barColor(line) {
      if (line.over_budget) return "error";
      const pct = this.percentUsed(line);
      if (pct >= 90) return "error";
      if (pct >= 75) return "warning";
      return "primary";
    },
  },
};
</script>

<style scoped>
.budget-over {
  color: var(--tf-error, #ef0405);
  font-weight: 600;
}
.budget-total {
  border-top: 2px solid var(--tf-border-strong, #d6d6d6);
  background: var(--tf-surface-alt, #fafbfc);
  font-weight: 600;
}
.budget-total__label {
  font-weight: 700;
  color: var(--tf-fg-1, #212121);
}
.budget-total__num {
  font-weight: 600;
}
.budget-total__pct {
  color: var(--tf-fg-2, #424242);
  font-weight: 600;
}
.budget-edit-cell {
  text-align: right;
  white-space: nowrap;
}
.budget-dialog__error {
  color: var(--tf-error, #ef0405);
}
.budget-dialog__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
